import type RadioButtonGroup from '@/components/form/RadioButtonGroup'
import type {
  RadioButtonGroupClassNames,
  RadioButtonGroupOption,
  RadioButtonGroupValue,
} from '@/components/form/RadioButtonGroup'
import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import { Check, X, type LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

const yesNoOptions: RadioButtonGroupOption[] = [
  {
    id: 'true',
    name: 'Yes',
    selectedTone: 'success',
    start: (
      <span className="inline-flex items-center self-center" aria-hidden="true">
        <Check className="size-3.5" />
      </span>
    ),
  },
  {
    id: 'false',
    name: 'No',
    selectedTone: 'destructive',
    start: (
      <span className="inline-flex items-center self-center" aria-hidden="true">
        <X className="size-3.5" />
      </span>
    ),
  },
]

const billingBooleanRadioClassNames: RadioButtonGroupClassNames = {
  container: 'grid w-full min-w-0 grid-cols-2',
  button: 'min-w-0 items-center justify-center gap-1.5 px-3.5 text-sm leading-none',
  label: 'font-medium',
}

export function BillingBooleanSettingField({
  className,
  description,
  disabled = false,
  displayValue,
  field,
  icon: Icon,
  title,
}: {
  className?: string
  description?: ReactNode
  disabled?: boolean
  displayValue?: RadioButtonGroupValue
  // The `form.AppField` render-prop field, which carries the registered field components.
  field: { RadioButtonGroup: typeof RadioButtonGroup }
  icon?: LucideIcon
  title: string
}) {
  return (
    <div className={cn('py-1', className)}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 gap-3">
          {Icon ? (
            <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          ) : null}
          <div className="min-w-0">
            <Typography variant="labelStrong" className="block">
              {title}
            </Typography>
            {description ? (
              <Typography variant="supporting" className="mt-1 block">
                {description}
              </Typography>
            ) : null}
          </div>
        </div>
        <field.RadioButtonGroup
          appearance="segmented"
          aria-label={title}
          className="w-full shrink-0 sm:w-auto"
          classNames={billingBooleanRadioClassNames}
          disabled={disabled}
          displayValue={displayValue}
          joined
          options={yesNoOptions}
          parseValue={(value) => value === 'true'}
          size="sm"
          unselectedVariant="outline"
        />
      </div>
    </div>
  )
}
